import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, CharacterView, PromptCapsule, GlowRing } from "./fx";

// SplitShare — tap the whole snack to break it in half, then carry a half over
// to a hungry friend, who lights up. Faithful port of iOS
// CityHelperSplitShareView.

const FRIEND_UNIT = { x: 0.26, y: 0.52 };
const COOKIE_UNIT = { x: 0.66, y: 0.52 };

const SplitShare = ({ payload, sounds, onDone }) => {
  const doneRef = useRef(false);
  const stageRef = useRef(null);
  const [split, setSplit] = useState(false);
  const [given, setGiven] = useState(false);
  const [dragLoc, setDragLoc] = useState(null);
  const [burst, setBurst] = useState(0);
  const [crumb, setCrumb] = useState(0);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const measure = useCallback(() => {
    const el = stageRef.current;
    if (el) setSize({ w: el.clientWidth, h: el.clientHeight });
  }, []);

  const point = (unit) => ({ x: unit.x * size.w, y: unit.y * size.h });
  const friendPt = point(FRIEND_UNIT);

  const doSplit = useCallback(() => {
    sounds.playPop(0.5);
    setCrumb((c) => c + 1);
    setSplit(true);
    sounds.speak("Now carry a half over to Ellie!");
  }, [sounds]);

  const handleDown = useCallback((e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = stageRef.current?.getBoundingClientRect();
    setDragLoc({ x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) });
  }, []);

  const handleMove = useCallback((e) => {
    setDragLoc((d) => {
      if (!d) return d;
      const rect = stageRef.current?.getBoundingClientRect();
      return { x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) };
    });
  }, []);

  const handleUp = useCallback(() => {
    setDragLoc((loc) => {
      if (!loc) return null;
      if (Math.hypot(loc.x - friendPt.x, loc.y - friendPt.y) < 130) {
        sounds.playPop(0.9);
        sounds.playSparkle();
        setGiven(true);
        setBurst((b) => b + 1);
        if (!doneRef.current) {
          doneRef.current = true;
          window.setTimeout(() => onDone(), 1200);
        }
        return friendPt;
      }
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendPt.x, friendPt.y, onDone, sounds]);

  return (
    <div className="ch-activity-body ch-split">
      <PromptCapsule text={split ? "Now carry a half over to Ellie!" : payload.prompt} sounds={sounds} />

      <div
        className="ch-stage"
        ref={stageRef}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        style={{ touchAction: "none" }}
      >
        <MeasureOnMount onMeasure={measure} />

        <div className="ch-split-friend" style={{ left: `${FRIEND_UNIT.x * 100}%`, top: `${FRIEND_UNIT.y * 100}%` }}>
          <GlowRing hot={!!dragLoc && !given} tint="#ffd45c" style={{ top: "88%" }} />
          <motion.div
            style={{ width: "100%", height: "100%" }}
            animate={dragLoc && !given ? { y: [0, -9, 0] } : { y: 0 }}
            transition={dragLoc && !given ? { duration: 0.65, repeat: Infinity, ease: "easeInOut" } : { duration: 0.3 }}
          >
            <CharacterView character={payload.friend} happy={given} />
          </motion.div>
          <SparkleBurst trigger={burst} reach={120} style={{ left: "50%", top: "12%" }} />
        </div>

        {!split ? (
          <button
            type="button"
            className="ch-split-whole"
            style={{ left: `${COOKIE_UNIT.x * 100}%`, top: `${COOKIE_UNIT.y * 100}%` }}
            onClick={doSplit}
            aria-label="Tap to break the snack"
          >
            <Sprite name={payload.wholeAsset || "ch_cookie_whole"} />
            <span className="ch-split-line" />
            <span className="ch-split-hint">Tap to break!</span>
          </button>
        ) : (
          !given && (
            <>
              <span
                className="ch-split-half kept"
                style={{ left: `${COOKIE_UNIT.x * 100}%`, top: `${COOKIE_UNIT.y * 100}%`, marginLeft: 44 }}
              >
                <motion.span
                  style={{ display: "block", width: "100%", height: "100%" }}
                  initial={{ x: -44, rotate: 0 }}
                  animate={{ x: 0, rotate: 8 }}
                  transition={{ type: "spring", stiffness: 240, damping: 13 }}
                >
                  <Sprite name={payload.halfAsset || "ch_cookie_half"} />
                </motion.span>
              </span>
              <div
                className="ch-split-half draggable"
                style={
                  dragLoc
                    ? { left: dragLoc.x, top: dragLoc.y }
                    : { left: `${COOKIE_UNIT.x * 100}%`, top: `${COOKIE_UNIT.y * 100}%`, marginLeft: -44 }
                }
                onPointerDown={handleDown}
                role="button"
                tabIndex={-1}
                aria-label="Carry a half to your friend"
              >
                <motion.span
                  style={{ display: "block", width: "100%", height: "100%" }}
                  initial={{ x: 44, rotate: 0 }}
                  animate={{ x: 0, rotate: dragLoc ? 0 : -8 }}
                  transition={{ type: "spring", stiffness: 240, damping: 13 }}
                >
                  <Sprite name={payload.halfAsset || "ch_cookie_half"} style={{ transform: "scaleX(-1)" }} />
                </motion.span>
              </div>
            </>
          )
        )}

        <SparkleBurst trigger={crumb} reach={60} style={{ left: `${COOKIE_UNIT.x * 100}%`, top: `${COOKIE_UNIT.y * 100}%` }} />
      </div>
    </div>
  );
};

// Small helper: measures the stage once mounted so unit->px math has real
// dimensions before the first drag.
const MeasureOnMount = ({ onMeasure }) => {
  const ref = useRef(null);
  React.useEffect(() => {
    onMeasure();
    window.addEventListener("resize", onMeasure);
    return () => window.removeEventListener("resize", onMeasure);
  }, [onMeasure]);
  return <span ref={ref} aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />;
};

export default SplitShare;
